/**
 * components/Shell.tsx
 * ------------------------------------------------------------------
 * The authenticated app frame: LKC sidebar (role-filtered), topbar
 * with year/term selector, and the routed content area.
 *
 * Menu visibility is derived from lib/permissions so the LKC rules
 * (editor flags/reports, HOY comments, subject teachers blocked from
 * reports, class teachers limited) are enforced in one place.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, type ReactNode } from "react";
import {
  FileMinus,
  CalendarClock,
  Users2,
  HeartHandshake,
  Home,
  Users,
  ListChecks,
  GraduationCap,
  Wallet,
  BookOpen,
  ClipboardList,
  Calendar,
  User,
  Flag,
  Printer,
  Lock,
  ShieldCheck,
  Database,
  Activity,
  Upload,
  LogOut,
  Menu,
  X,
  Send,
  PenLine,
  FileText,
  FileSpreadsheet,
  ClipboardCheck,
  Loader2,
  UserCog,
  PanelLeftClose,
  PanelLeftOpen,
  UserPlus,
  ShieldAlert,
  Award,
  ArrowUpDown,
  ChevronDown,
  Table2,
  Megaphone,
  MessageSquare,
  Clock,
  Star,
  Calculator,
  Banknote,
  Landmark,
  Receipt,
  Truck,
  ScanSearch,
  Waves,
  TrendingUp,
  Scale,
  CalendarCheck,
  HelpCircle,
  CalendarRange,
  type LucideIcon,
  UserMinus,
  LayoutDashboard,
  SlidersHorizontal,
  Play,
  HandCoins,
  HeartPulse,
  ArrowLeftRight,
  BellRing,
  FileUp,
  Inbox,
  MailPlus,
  Camera,
  FilePlus2,
  IdCard,
  UserRoundCheck,
  ListTree,
  BadgePercent,
  BedDouble,
  FileQuestion,
  Mail,
  Archive,
  Wrench,
  Target,
  Boxes,
  ChartPie,
  ReceiptText,
  Repeat,
  FolderOpen,
  Presentation,
  NotebookPen,
  BookOpenCheck,
  BookMarked,
  LayoutGrid,
  CalendarDays,
  CalendarCheck2,
  UserCheck,
  Grid3x3,
  BookX,
  Library,
  FlaskConical,
  CalendarCog,
} from "lucide-react";
import { launchNako } from "@/features/timetable/NakoLaunch";
import { useAuth } from "@/contexts/AuthContext";
import { SCHOOL, TermKey } from "@/lib/school.config";
import { ROLES, getPermissions } from "@/lib/permissions";
import SchoolLogo from "@/components/ui/SchoolLogo";
import NotificationsPanel from "@/features/notifications/NotificationsPanel";
import PushEnrollButton from "@/features/notifications/PushEnrollButton";
import ThemeToggle from "@/components/ThemeToggle";
import SessionTimeoutModal from "@/components/SessionTimeoutModal";
import BackupHealthAlert from "@/components/BackupHealthAlert";
import type { Permissions } from "@/lib/permissions";

export type TabId =
  | "home"
  | "teachers"
  | "students"
  | "teacherSwap"
  | "families"
  | "subjects"
  | "classes"
  | "admissions"
  | "discipline"
  | "achievements"
  | "attendance"
  | "marks"
  | "classComment"
  | "hodComment"
  | "flags"
  | "commentReview"
  | "progress"
  | "reports"
  | "viewReports"
  | "bulkSend"
  | "terms"
  | "users"
  | "audit"
  | "activity"
  | "parentContacts"
  | "bulkImport"
  | "bulkComments"
  | "promotion"
  | "parentPortal"
  | "parentFees"
  | "parentMessages"
  | "schoolComms"
  | "parentSettings"
  | "termDates"
  | "announcements"
  | "schoolMailbox"
  | "photoManagement"
  | "parentNotices"
  | "messageParents"
  // Teaching + study portal, ported from Al-Nur (TEACHING-AND-STUDENT-PORTAL-PLAN.md)
  | "teachingLessons"
  | "homework"
  // Textbook Issue & Returns (proposal module 5) — one screen, three doors
  | "textbooks"
  | "textbookCharges"
  | "textbookClearance"
  | "markbook"
  | "schemes"
  | "lessonPlans"
  | "studyProgress"
  | "marking"
  | "studyLibrary"
  | "practicals"
  | "syllabusEditor"
  | "studyPortal"
  | "setupHub"
  | "parentOnboarding"
  | "teacherAnalysis"
  | "marksCompliance"
  | "marksheet"
  | "headInsight"
  | "exams"
  // Exams, from Al-Nur (30 Oct 2026): one screen per entry
  | "examTimetable"
  | "examTimetables"
  | "internalExams"
  | "examRegistration"
  | "examTools"
  | "teachingDays"
  | "myTimetable"
  | "lessonRegister"
  | "cover"
  | "staffing"
  | "subjectChoices"
  // Nako, the timetable editor: its own site, opened signed in (2 Nov 2026)
  | "nako"
  | "invigilation"
  | "myInvigilation"
  | "examRegisters"
  | "examSeating"
  | "examRoom"
  | "templates"
  | "account"
  | "studentProfile"
  | "alumni"
  | "teacherProfile"
  | "assessmentConfig"
  | "hrStaff"
  | "hrCompliance"
  | "hrClocking"
  | "hrClockingImport"
  | "hrLeave"
  | "hrContracts"
  | "hrAppraisals"
  | "hrPayroll"
  | "hrSeverance"
  | "hrLoans"
  | "hrMedicalAid"
  | "hrPaymentDetails"
  | "hrPayRun"
  | "hrDeductionReports"
  | "hrTaxTables"
  | "hrBurs"
  | "financeOverview"
  | "financeFees"
  | "financeSpecialCases"
  | "financeBilling"
  | "financeInvoice"
  | "financeExamFees"
  | "financePayments"
  | "financeUnidentified"
  | "financeCashUp"
  | "financeStatements"
  | "financeArrears"
  | "financeAgeAnalysis"
  | "financeDebtors"
  | "financeArrangements"
  | "financeAdjustments"
  | "financeSiblingDiscounts"
  | "financeReconcile"
  | "financeChartOfAccounts"
  | "financeJournals"
  | "financeGeneralLedger"
  | "financeOpeningBalances"
  | "financePeriodEnd"
  | "financeTrialBalance"
  | "financeIncomeExp"
  | "financeBalanceSheet"
  | "financeCashFlow"
  | "financeControlAccounts"
  | "financeAnomalies"
  | "financeSuppliers"
  | "financeVouchers"
  | "financeRequisitions"
  | "financePurchaseOrders"
  | "financePaymentBatches"
  | "financeFeeStatus"
  | "financeAnalysis"
  | "financeAssets"
  | "financeBudgets"
  | "financeHousekeeping"
  | "financeFinancialYear"
  | "financeAuditPack"
  | "financeTaxVat"
  | "financeDetailedLedger"
  | "financePartyJournals"
  | "financeDocuments"
  | "financeBankProof"
  | "financeCashBook"
  | "financeStandingBills"
  | "financePettyCash"
  | "financeRemittance"
  | "financeCreditorsAging"
  | "financeSupplierStatements"
  | "myBoarders"
  | "publicHolidays"
  | "staffProfile"
  | "myPayHr";

interface NavItem {
  id: TabId;
  label: string;
  icon: LucideIcon;
  section:
    | "main"
    // A teacher's day: timetable, registers, cover, exam duties (from Al-Nur,
    // 31 Oct 2026).
    | "schoolDay"
    // A teacher's own work, in the order it is done (1 Nov 2026): plan it,
    // teach it, assess it. One "Teaching" group of thirteen became three.
    | "plan"
    | "teach"
    | "assess"
    // What a HOD answers for: cover, the department's results and marks, its
    // syllabuses and next year's staffing. Leadership see every department.
    | "department"
    // The report cycle, from marks to sending (was "Data Entry" + "Reports").
    | "reports"
    | "pastoral"
    | "setup"
    | "exams"
    | "academicAdmin"
    | "comms"
    // --- the money, in the order it moves ---------------------------
    // One flat "finance" section became eight. It held eleven screens and
    // is about to hold sixty; a single scroll of sixty items is a list
    // nobody reads to the bottom of. The grouping follows the direction
    // money travels rather than the order the screens were built.
    | "feeBilling"
    | "feeCollect"
    | "purchasing"
    | "payables"
    | "cashbank"
    | "ledger"
    | "finReports"
    | "finSetup"
    | "hr"
    | "payroll"
    | "system"
    | "schoolSettings";
  /** permission key gating visibility */
  gate?: keyof Permissions;
}

/**
 * Exported so the App can tell a tab that is DELIBERATELY handled by the render
 * switch's default from one whose screen was never wired up. Without that
 * distinction a missing case renders the Dashboard and looks like it worked.
 */
export const NAV: NavItem[] = [
  { id: "home", label: "Dashboard", icon: Home, section: "main" },
  {
    id: "parentPortal",
    label: "My Children",
    icon: GraduationCap,
    section: "main",
    gate: "canAccessParentPortal",
  },
  {
    id: "parentFees",
    label: "Fees",
    icon: Wallet,
    section: "main",
    gate: "canAccessParentPortal",
  },
  {
    id: "parentMessages",
    label: "Messages",
    icon: MessageSquare,
    section: "main",
    gate: "canAccessParentPortal",
  },
  { id: "account", label: "Account", icon: UserCog, section: "main" },
  { id: "myPayHr", label: "My Pay & HR", icon: IdCard, section: "main" },

  {
    id: "setupHub",
    label: "Setup",
    icon: ClipboardList,
    section: "setup",
    gate: "canAccessSetup",
  },
  {
    id: "admissions",
    label: "Admissions",
    icon: UserPlus,
    section: "setup",
    gate: "canAccessAdmissions",
  },
  {
    // Leavers still holding school textbooks.
    id: "textbookClearance",
    label: "Textbook clearance",
    icon: BookX,
    section: "setup",
    gate: "canAccessAdmissions",
  },
  {
    id: "teachers",
    label: "Teachers",
    icon: Users,
    section: "setup",
    gate: "canManageTeachers",
  },
  {
    id: "photoManagement",
    label: "Photo Management",
    icon: Camera,
    section: "setup",
    gate: "canReviewPupilPhotos",
  },
  {
    id: "students",
    label: "Students",
    icon: GraduationCap,
    section: "setup",
    gate: "canManageStudents",
  },
  {
    id: "alumni",
    label: "Alumni",
    icon: GraduationCap,
    section: "setup",
    gate: "canManageStudents",
  },
  {
    id: "teacherSwap",
    label: "Teacher Swap",
    icon: ArrowLeftRight,
    section: "setup",
    gate: "canManageStudents",
  },
  {
    id: "families",
    label: "Families",
    icon: Users,
    section: "setup",
    gate: "canManageFamilies",
  },
  {
    id: "subjects",
    label: "Subjects",
    icon: BookOpen,
    section: "setup",
    gate: "canManageSubjects",
  },
  {
    id: "parentOnboarding",
    label: "Parent Onboarding",
    icon: UserRoundCheck,
    section: "schoolSettings",
    gate: "canManageParentSettings",
  },
  {
    id: "classes",
    label: "Classes",
    icon: ClipboardList,
    section: "setup",
    gate: "canManageClasses",
  },

  {
    id: "attendance",
    label: "Attendance",
    icon: Calendar,
    section: "schoolDay",
    gate: "canEnterClassTeacherComment",
  },
  {
    id: "marks",
    label: "Mark Entry",
    icon: ClipboardList,
    section: "reports",
    gate: "canEnterMarks",
  },
  {
    id: "classComment",
    label: "Class Comment",
    icon: User,
    section: "reports",
    gate: "canEnterClassTeacherComment",
  },
  {
    id: "hodComment",
    label: "HOY Comment",
    icon: PenLine,
    section: "reports",
    gate: "canEnterHodComment",
  },
  {
    id: "commentReview",
    label: "Comment Review",
    icon: ClipboardCheck,
    section: "reports",
    gate: "canViewCommentReview",
  },
  {
    id: "flags",
    label: "Comment Flags",
    icon: Flag,
    section: "reports",
    gate: "canRaiseFlags",
  },
  {
    id: "discipline",
    label: "Discipline",
    icon: ShieldAlert,
    section: "pastoral",
    gate: "canManageDiscipline",
  },
  {
    id: "achievements",
    label: "Achievements",
    icon: Award,
    section: "pastoral",
    gate: "canManageAchievements",
  },
  /* ── Teaching — from Al-Nur, sharing its lessons, syllabuses and question
     banks (seed/sync-from-alnur.cjs). One gate for the lot, held by the staff
     who teach or oversee classes; each screen narrows to what the teacher
     teaches (useTeachingScope). ─────────────────────────────────────────── */
  {
    // Interactive lessons, to present on the board.
    id: "teachingLessons",
    label: "Lessons",
    icon: Presentation,
    section: "teach",
    gate: "canViewStudyProgress",
  },
  {
    // Tasks and handouts for a class, and the study portal's set work.
    id: "homework",
    label: "Homework & Tests",
    icon: NotebookPen,
    section: "teach",
    gate: "canViewStudyProgress",
  },
  {
    // Teachers write these, HODs approve them.
    id: "schemes",
    label: "Schemes of Work",
    icon: ClipboardList,
    section: "plan",
    gate: "canViewStudyProgress",
  },
  {
    // The same documents as Schemes, from the other end: what am I teaching
    // this week, and is it planned?
    id: "lessonPlans",
    label: "Lesson Plans",
    icon: BookOpenCheck,
    section: "plan",
    gate: "canViewStudyProgress",
  },
  {
    // Every mark a teacher has entered this year, printable and exportable.
    // LKC's monthly CA shows as its own columns.
    id: "markbook",
    label: "Markbook",
    icon: BookMarked,
    section: "assess",
    gate: "canViewStudyProgress",
  },
  {
    // The timetable (from TimeTabler, on the 6-day cycle). Everyone here sees
    // their own; looking up any class, teacher or room is gated inside the
    // screen on canViewAllTimetables (admin, head, HODs, HOYs).
    id: "myTimetable",
    label: "Timetable",
    icon: CalendarDays,
    section: "schoolDay",
    gate: "canViewStudyProgress",
  },
  {
    // A register per lesson, with attendance history and teaching groups.
    // A teacher takes the registers of their own lessons (rules: teachesSlot).
    id: "lessonRegister",
    label: "Lesson Register",
    icon: ClipboardCheck,
    section: "schoolDay",
    gate: "canViewStudyProgress",
  },
  {
    // Next year's staffing: who teaches what. HODs for their departments.
    id: "staffing",
    label: "Staffing",
    icon: Users,
    section: "department",
    gate: "canEditStaffing",
  },
  {
    // Next year's IGCSE and AS subject choices from families. Leadership run
    // the window; HODs read the choices and write their subjects' descriptions.
    id: "subjectChoices",
    label: "Subject Choices",
    icon: ListChecks,
    section: "department",
    gate: "canEditStaffing",
  },
  {
    // Cover for an absent teacher: admin, the head, academic admins, HODs.
    id: "cover",
    label: "Cover",
    icon: UserCheck,
    section: "department",
    gate: "canManageCover",
  },
  {
    // Issue textbooks to the pupils a teacher teaches; Heads of Year read.
    id: "textbooks",
    label: "Textbooks",
    icon: Library,
    section: "teach",
    gate: "canViewStudyProgress",
  },
  {
    id: "studyProgress",
    label: "Class Progress",
    icon: ScanSearch,
    section: "assess",
    gate: "canViewStudyProgress",
  },
  {
    // Every written paper waiting to be marked or released.
    id: "marking",
    label: "Marking",
    icon: ClipboardCheck,
    section: "assess",
    gate: "canViewStudyProgress",
  },
  {
    id: "studyLibrary",
    label: "Question Library",
    icon: Library,
    section: "assess",
    gate: "canViewStudyProgress",
  },
  {
    // Practical and alternative-to-practical questions; compiles a paper,
    // the technician's sheet and a marking key.
    id: "practicals",
    label: "Practicals",
    icon: FlaskConical,
    section: "teach",
    gate: "canViewStudyProgress",
  },
  {
    // Subjects and their objectives, layered over the synced syllabuses.
    // Only the people who edit it see it.
    id: "syllabusEditor",
    label: "Subjects & Syllabuses",
    icon: Library,
    section: "department",
    gate: "canManageCurriculum",
  },

  {
    id: "marksheet",
    label: "Student Analysis",
    icon: Table2,
    section: "reports",
    gate: "canViewProgress",
  },
  {
    id: "teacherAnalysis",
    label: "Teacher & Dept",
    icon: Users,
    section: "reports",
    gate: "canViewProgress",
  },
  {
    id: "marksCompliance",
    label: "Marks Compliance",
    icon: ClipboardCheck,
    section: "reports",
    gate: "canViewProgress",
  },
  {
    // The session about to be run, and what is coming — the exam officer's.
    id: "exams",
    label: "Exam days",
    icon: FileText,
    section: "exams",
    gate: "canManageExamTimetables",
  },
  {
    // The dates, for every teacher.
    id: "examTimetable",
    label: "Exam timetable",
    icon: CalendarClock,
    section: "exams",
    gate: "canViewExams",
  },
  {
    // The rota, session by session — the exam officer's.
    id: "invigilation",
    label: "Invigilation",
    icon: ShieldCheck,
    section: "exams",
    gate: "canManageExamTimetables",
  },
  {
    // A teacher's own duties.
    id: "myInvigilation",
    label: "My exam duties",
    icon: UserCheck,
    section: "schoolDay",
    gate: "canViewExams",
  },
  {
    // Who should be in the room, and who was — filled in the hall.
    id: "examRegisters",
    label: "Registers",
    icon: ClipboardCheck,
    section: "exams",
    gate: "canViewExams",
  },
  {
    // The rooms, and a seating plan per session.
    id: "examSeating",
    label: "Rooms & seating",
    icon: Grid3x3,
    section: "exams",
    gate: "canViewExams",
  },
  {
    // The projected clock in the exam room.
    id: "examRoom",
    label: "Exam room screen",
    icon: Presentation,
    section: "exams",
    gate: "canViewExams",
  },
  {
    // The school's own exams (End of Year), from the Examinations Unit.
    id: "internalExams",
    label: "Internal exams",
    icon: ClipboardList,
    section: "exams",
    gate: "canManageExamTimetables",
  },
  {
    // A Cambridge series: which classes, which papers, publishing.
    id: "examTimetables",
    label: "Cambridge series",
    icon: Table2,
    section: "exams",
    gate: "canManageExamTimetables",
  },
  {
    // The candidate file for Cambridge Direct, from the Examinations Unit.
    id: "examRegistration",
    label: "Registration",
    icon: FileSpreadsheet,
    section: "exams",
    gate: "canManageExamTimetables",
  },
  {
    // Class lists, registers and seating plans — LKC's earlier screen.
    id: "examTools",
    label: "Class lists & seating",
    icon: LayoutGrid,
    section: "exams",
    gate: "canManageExams",
  },
  {
    id: "reports",
    label: "Generate Reports",
    icon: Printer,
    section: "reports",
    gate: "canGenerateReports",
  },
  {
    id: "viewReports",
    label: "View Reports",
    icon: FileText,
    section: "reports",
    gate: "canViewLiveAcademics",
  },
  {
    id: "bulkSend",
    label: "Send Reports",
    icon: Send,
    section: "reports",
    gate: "canBulkSendReports",
  },

  {
    // Opens Nako, the timetable editor, in its own tab, signed in as the
    // timetabler (features/timetable/NakoLaunch.tsx). The timetabling roles.
    id: "nako",
    label: "Timetable editor",
    icon: CalendarCog,
    section: "academicAdmin",
    gate: "canOpenNako",
  },
  {
    id: "terms",
    label: "Terms",
    icon: Lock,
    section: "academicAdmin",
    gate: "canManageTerms",
  },
  {
    id: "promotion",
    label: "Promotion",
    icon: ArrowUpDown,
    section: "academicAdmin",
    gate: "canManageTerms",
  },
  {
    id: "bulkComments",
    label: "Bulk Comment Import",
    icon: Upload,
    section: "academicAdmin",
    gate: "canBulkImport",
  },
  {
    id: "templates",
    label: "Comment Templates",
    icon: FileText,
    section: "academicAdmin",
    gate: "canManageTerms",
  },
  {
    id: "users",
    label: "User Management",
    icon: ShieldCheck,
    section: "system",
    gate: "canManageTeacherAccounts",
  },
  {
    id: "audit",
    label: "Data & Backups",
    icon: Database,
    section: "system",
    gate: "canViewAudit",
  },
  {
    id: "activity",
    label: "Activity Log",
    icon: Activity,
    section: "system",
    gate: "canViewActivityLog",
  },
  {
    id: "bulkImport",
    label: "Bulk Import",
    icon: Upload,
    section: "system",
    gate: "canBulkImport",
  },
  {
    id: "parentSettings",
    label: "Parent Settings",
    icon: Users,
    section: "schoolSettings",
    gate: "canManageParentSettings",
  },
  {
    id: "schoolComms",
    label: "Parent Messages",
    icon: MessageSquare,
    section: "comms",
    gate: "canManageParentSettings",
  },
  {
    id: "termDates",
    label: "Term Dates",
    icon: CalendarRange,
    section: "schoolSettings",
    gate: "canManageTerms",
  },
  {
    // The 6-day timetable cycle: where it counts from, and the days it pauses.
    id: "teachingDays",
    label: "Teaching Days",
    icon: CalendarCheck2,
    section: "schoolSettings",
    gate: "canManageTerms",
  },
  {
    id: "announcements",
    label: "Announcements",
    icon: Megaphone,
    section: "schoolSettings",
    gate: "canManageTerms",
  },
  {
    id: "publicHolidays",
    label: "Public Holidays",
    icon: CalendarRange,
    section: "schoolDay",
    gate: "canAccessHR",
  },
  {
    id: "parentContacts",
    label: "Missing Parent Emails",
    icon: MailPlus,
    section: "comms",
    gate: "canCollectParentContacts",
  },
  {
    id: "schoolMailbox",
    label: "School Mailbox",
    icon: Inbox,
    section: "comms",
    gate: "canReadSchoolMail",
  },
  {
    id: "parentNotices",
    label: "Parent Notices",
    icon: BellRing,
    section: "comms",
    gate: "canManageTerms",
  },
  {
    id: "messageParents",
    label: "Message Parents",
    icon: Send,
    section: "comms",
    gate: "canMessageParents",
  },

  // HR DOMAIN — gated to the `hr` role + admin via canAccessHR.
  // Invisible to teachers, parents, and academic-only staff. Remove
  // this whole block to take HR out of the nav.
  {
    id: "hrStaff",
    label: "Staff Directory",
    icon: Users,
    section: "hr",
    gate: "canAccessHR",
  },
  {
    id: "hrCompliance",
    label: "Compliance",
    icon: ShieldCheck,
    section: "hr",
    gate: "canAccessHR",
  },
  {
    id: "hrClocking",
    label: "Clocking",
    icon: Clock,
    section: "hr",
    gate: "canViewClocking",
  },
  {
    id: "hrClockingImport",
    label: "Clocking Upload",
    icon: FileUp,
    section: "hr",
    gate: "canImportClocking",
  },
  {
    id: "hrLeave",
    label: "Leave",
    icon: Calendar,
    section: "hr",
    gate: "canAccessHR",
  },
  {
    id: "hrContracts",
    label: "Contracts",
    icon: FileText,
    section: "hr",
    gate: "canAccessHR",
  },
  {
    id: "hrAppraisals",
    label: "Appraisals",
    icon: Star,
    section: "hr",
    gate: "canAccessHR",
  },
  {
    id: "hrPayroll",
    label: "Payroll",
    icon: Banknote,
    section: "payroll",
    gate: "canAccessPayroll",
  },
  {
    id: "hrSeverance",
    label: "Severance",
    icon: UserMinus,
    section: "payroll",
    gate: "canAccessPayroll",
  },
  {
    id: "hrLoans",
    label: "Loans & Advances",
    icon: HandCoins,
    section: "payroll",
    gate: "canAccessPayroll",
  },
  {
    id: "hrMedicalAid",
    label: "Medical Aid",
    icon: HeartPulse,
    section: "payroll",
    gate: "canAccessPayroll",
  },
  {
    id: "hrPaymentDetails",
    label: "Payment Details",
    icon: Landmark,
    section: "payroll",
    gate: "canAccessPayroll",
  },
  {
    id: "hrTaxTables",
    label: "Tax Tables",
    icon: Calculator,
    section: "payroll",
    gate: "canManageTaxTables",
  },
  {
    id: "hrBurs",
    label: "BURS Returns",
    icon: Landmark,
    section: "payroll",
    gate: "canAccessPayroll",
  },

  // FINANCE DOMAIN — separate nav entries (like HR); each page can hold
  // its own sub-tabs. Gated to finance staff; Fees is config-only.
  {
    id: "financeOverview",
    label: "Overview",
    icon: LayoutDashboard,
    section: "feeBilling",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeFees",
    label: "Fees",
    icon: SlidersHorizontal,
    section: "feeBilling",
    gate: "canManageFinanceConfig",
  },
  {
    id: "financeSpecialCases",
    label: "Special Cases",
    icon: HeartHandshake,
    section: "feeBilling",
    gate: "canAccessFinance",
  },
  {
    id: "financeSiblingDiscounts",
    label: "Sibling Discounts",
    icon: BadgePercent,
    section: "feeBilling",
    gate: "canAccessFinance",
  },
  {
    id: "financeBilling",
    label: "Billing",
    icon: Play,
    section: "feeBilling",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeInvoice",
    label: "New invoice",
    icon: FilePlus2,
    section: "feeBilling",
    gate: "canAccessFinance",
  },
  {
    // Lost or ruined textbooks: charge the family in one click, or waive.
    id: "textbookCharges",
    label: "Lost textbooks",
    icon: BookX,
    section: "feeBilling",
    gate: "canAccessFinance",
  },
  {
    id: "financeExamFees",
    label: "Bulk Invoices",
    icon: FileSpreadsheet,
    section: "feeBilling",
    gate: "canAccessFinance",
  },
  {
    id: "financePayments",
    label: "Payments",
    icon: HandCoins,
    section: "feeCollect",
    gate: "canAccessFinance",
  },
  {
    id: "financeUnidentified",
    label: "Unidentified",
    icon: HelpCircle,
    section: "feeCollect",
    gate: "canAccessFinance",
  },
  {
    id: "financeCashUp",
    label: "Cash Up",
    icon: Calculator,
    section: "feeCollect",
    gate: "canAccessFinance",
  },
  {
    id: "financeStatements",
    label: "Statements",
    icon: FileText,
    section: "feeCollect",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeDebtors",
    label: "Debtors",
    icon: Users2,
    section: "feeCollect",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeArrangements",
    label: "Payment Plans",
    icon: CalendarClock,
    section: "feeCollect",
    gate: "canAccessFinance",
  },
  {
    id: "financeAdjustments",
    label: "Credit Notes",
    icon: FileMinus,
    section: "feeCollect",
    gate: "canAccessFinance",
  },
  {
    id: "financeArrears",
    label: "Arrears",
    icon: BellRing,
    section: "feeCollect",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeAgeAnalysis",
    label: "Pastel Check",
    icon: Scale,
    section: "feeCollect",
    gate: "canImportPastel",
  },
  {
    id: "financeReconcile",
    label: "Reconcile",
    icon: ArrowLeftRight,
    section: "cashbank",
    gate: "canAccessFinance",
  },
  {
    id: "myBoarders",
    label: "My Boarders",
    icon: BedDouble,
    section: "main",
    gate: "canViewBoarderReports",
  },
  {
    id: "financeJournals",
    label: "Journals",
    icon: BookOpen,
    section: "ledger",
    gate: "canViewGeneralLedger",
  },
  {
    id: "financeGeneralLedger",
    label: "General Ledger",
    icon: Landmark,
    section: "ledger",
    gate: "canViewGeneralLedger",
  },
  {
    id: "financeOpeningBalances",
    label: "Opening Balances",
    icon: Landmark,
    section: "ledger",
    gate: "canAccessFinance",
  },
  {
    id: "financePeriodEnd",
    label: "Period End",
    icon: CalendarCheck,
    section: "ledger",
    gate: "canAccessFinance",
  },
  {
    id: "financeControlAccounts",
    label: "Control Accounts",
    icon: ShieldCheck,
    section: "finReports",
    gate: "canViewGeneralLedger",
  },
  {
    id: "financeAnomalies",
    label: "Anomaly Review",
    icon: ScanSearch,
    section: "finReports",
    gate: "canViewGeneralLedger",
  },
    {
    id: "financeVouchers",
    label: "Payment Vouchers",
    icon: Receipt,
    section: "purchasing",
    gate: "canRaiseVouchers",
  },
{
    id: "financeSuppliers",
    label: "Suppliers",
    icon: Truck,
    section: "purchasing",
    gate: "canManageSuppliers",
  },
  {
    id: "financeRequisitions",
    label: "Requisitions",
    icon: FileQuestion,
    section: "purchasing",
  },
  {
    id: "financePurchaseOrders",
    label: "Purchase Orders",
    icon: ClipboardList,
    section: "purchasing",
    gate: "canRaiseVouchers",
  },
  {
    id: "financePaymentBatches",
    label: "Payment Runs",
    icon: Landmark,
    section: "purchasing",
    gate: "canReleasePayments",
  },
  {
    id: "financeFeeStatus",
    label: "Fee Status",
    icon: ReceiptText,
    section: "feeBilling",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeAnalysis",
    label: "Analysis",
    icon: ChartPie,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeAssets",
    label: "Asset Register",
    icon: Boxes,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeBudgets",
    label: "Budgets",
    icon: Target,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeHousekeeping",
    label: "Housekeeping",
    icon: Wrench,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeFinancialYear",
    label: "Financial Year",
    icon: CalendarCheck,
    section: "finReports",
    gate: "canPostJournals",
  },
  {
    id: "financeAuditPack",
    label: "Audit Pack",
    icon: Archive,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeTaxVat",
    label: "VAT",
    icon: Receipt,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeDetailedLedger",
    label: "Detailed Ledger",
    icon: BookOpen,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financePartyJournals",
    label: "Debtor & Creditor Journals",
    icon: Scale,
    section: "finReports",
    gate: "canPostJournals",
  },
  {
    id: "financeBankProof",
    label: "Bank Proof",
    icon: Scale,
    section: "cashbank",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeDocuments",
    label: "Documents",
    icon: FolderOpen,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeCashBook",
    label: "Cash Book",
    icon: BookOpen,
    section: "purchasing",
    gate: "canPostJournals",
  },
  {
    id: "financeStandingBills",
    label: "Standing Bills",
    icon: Repeat,
    section: "purchasing",
    gate: "canRaiseVouchers",
  },
  {
    id: "financePettyCash",
    label: "Petty Cash",
    icon: Wallet,
    section: "purchasing",
    gate: "canRaiseVouchers",
  },
  {
    id: "financeRemittance",
    label: "Remittance Advice",
    icon: Mail,
    section: "purchasing",
    gate: "canReleasePayments",
  },
  {
    id: "financeCreditorsAging",
    label: "Creditors Aging",
    icon: Clock,
    section: "purchasing",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeSupplierStatements",
    label: "Supplier Statements",
    icon: FileText,
    section: "purchasing",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeTrialBalance",
    label: "Trial Balance",
    icon: Scale,
    section: "finReports",
    gate: "canViewGeneralLedger",
  },
  {
    id: "financeIncomeExp",
    label: "Income & Expenditure",
    icon: TrendingUp,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeBalanceSheet",
    label: "Balance Sheet",
    icon: Landmark,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeCashFlow",
    label: "Cash Flow",
    icon: Waves,
    section: "finReports",
    gate: "canViewFinanceTotals",
  },
  {
    id: "financeChartOfAccounts",
    label: "Chart of Accounts",
    icon: ListTree,
    section: "finSetup",
    gate: "canManageChartOfAccounts",
  },

];

/**
 * The order sections appear in the sidebar — and, because the Dashboard reads
 * this same constant, the order its card groups appear in too.
 *
 * Exported for that reason. The Dashboard used to keep its own list of sections
 * and the two drifted: the sidebar spoke of one "Finance" heading while the
 * dashboard spoke of "Fees" and "Accounting". A card now belongs to whichever
 * section its tab belongs to, so they cannot disagree again.
 */
export const SECTION_ORDER = [
  "main",
  // The school day (from Al-Nur, 31 Oct 2026): the timetable, the registers
  // taken from it, cover for an absent colleague and a teacher's own exam
  // duties — one place for one day's work, rather than four groups.
  "schoolDay",
  "plan",
  "teach",
  "assess",
  "department",
  "reports",
  "pastoral",
  "setup",
  "exams",
  "academicAdmin",
  "comms",
  // --- the money, in the order it moves ------------------------------
  "feeBilling",
  "feeCollect",
  "purchasing",
  "payables",
  "cashbank",
  "ledger",
  "finReports",
  "finSetup",
  "hr",
  "payroll",
  "system",
  "schoolSettings",
] as const;

export const SECTION_LABELS: Record<NavItem["section"], string> = {
  main: "",
  schoolDay: "School Day",
  plan: "Plan",
  teach: "Teach",
  assess: "Assess",
  department: "My Department",
  reports: "Reports",
  pastoral: "Pastoral",
  setup: "Setup",
  exams: "Exams",
  academicAdmin: "Academic Admin",
  comms: "Communications",

  // --- the money, in the order it moves ------------------------------
  // Empty sections render nothing (see the nav loop below), so the ones
  // waiting on later blocks stay invisible until a screen lands in them.
  feeBilling: "Fees — Billing",
  feeCollect: "Fees — Collections",
  purchasing: "Purchasing & Suppliers",
  payables: "Payments Out",
  cashbank: "Cash & Bank",
  ledger: "The Ledger",
  finReports: "Reports & Compliance",
  finSetup: "Finance Setup",

  hr: "HR",
  payroll: "Payroll",
  system: "System",
  schoolSettings: "School Settings",
};

/** A HOD has one department (or a few of their own); leadership oversee all
 *  of them, so the same group is "Departments" for them. */
const LEADERSHIP = ["admin", "systemAdmin", "academicAdmin", "superAcademicAdmin", "headteacher"];
export function sectionLabel(sec: NavItem["section"], roles: readonly string[]): string {
  if (sec === "department" && roles.some((r) => LEADERSHIP.includes(r))) return "Departments";
  return SECTION_LABELS[sec];
}

/** Within a group, the order the work is done in; tabs not listed follow in
 *  NAV order. */
const ITEM_ORDER: Partial<Record<NavItem["section"], TabId[]>> = {
  // A day in the order it runs: the timetable, the daily register, a
  // lesson's register, exam duties, then the holidays.
  schoolDay: ["myTimetable", "attendance", "lessonRegister", "myInvigilation", "publicHolidays"],
  teach: ["teachingLessons", "homework", "practicals", "textbooks"],
  assess: ["marking", "markbook", "studyProgress", "studyLibrary"],
  // Today's cover first, then this term's results, then next year.
  department: ["cover", "teacherAnalysis", "marksCompliance", "syllabusEditor", "staffing", "subjectChoices"],
};
const rankIn = (sec: NavItem["section"], id: TabId) => {
  const order = ITEM_ORDER[sec] ?? [];
  const k = order.indexOf(id);
  return k < 0 ? order.length : k;
};
/** The department's results screens sit under My Department for whoever has
 *  the group; a Head of Year, who reads them too, finds them under Reports. */
const DEPARTMENT_RESULTS: TabId[] = ["teacherAnalysis", "marksCompliance"];

/** The group a tab sits in for this person — the sidebar's and the
 *  Dashboard's, so the two cannot disagree. Anyone with no teaching day of
 *  their own (HR, the bursar) keeps the School Day links among the personal
 *  tools: a "School Day" heading over one link is noise. My Department is for
 *  the people who run one: HODs and leadership (the same people who set
 *  staffing); Cover falls back to School Day for anyone else. */
export function placeTab(tab: TabId, perms: Permissions, linkedTeacherId?: string | null): NavItem["section"] {
  const sec = NAV.find((n) => n.id === tab)?.section ?? "system";
  const staffDay = !!linkedTeacherId || perms.canViewAllTimetables || perms.canManageCover || perms.canEnterClassTeacherComment;
  const inDepartment = perms.canEditStaffing;
  if (sec === "schoolDay" && !staffDay) return "main";
  if (inDepartment && DEPARTMENT_RESULTS.includes(tab)) return "department";
  if (sec === "department" && !inDepartment) return tab === "cover" ? "schoolDay" : "plan";
  return sec;
}

interface ShellProps {
  active: TabId;
  onNavigate: (t: TabId) => void;
  year: string;
  term: TermKey;
  onYearChange: (y: string) => void;
  onTermChange: (t: TermKey) => void;
  children: ReactNode;
}

export default function Shell({
  active,
  onNavigate,
  year,
  term,
  onYearChange,
  onTermChange,
  children,
}: ShellProps) {
  const { userData, userRole, userRoles, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Track which nav sections are expanded (collapsible accordion)
  const perms = getPermissions(userRoles);
  const visible = NAV.filter((i) => {
    // Self-service: visible only to users linked to a staff record.
    if (i.id === "myPayHr") return !!userData?.linkedStaffId;
    // All other sections: standard perm gate
    return !i.gate || perms[i.gate];
  });
  const roleMeta = userRole ? ROLES[userRole] : null;

  // Off SECTION_ORDER, not a second hand-kept list beside it. The inline copy
  // that used to live here was one of the two lists that drifted apart.
  const sectionOfItem = (i: NavItem) => placeTab(i.id, perms, userData?.linkedTeacherId);
  // Auto-expand the section containing the active tab, where this person sees it
  const activeItem = NAV.find((i) => i.id === active);
  const activeSection = activeItem ? sectionOfItem(activeItem) : "main";
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    // School Day opens by default: Timetable is a teacher's first click.
    new Set([activeSection, "schoolDay"]),
  );

  function toggleSection(sec: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sec)) next.delete(sec);
      else next.add(sec);
      return next;
    });
  }

  // Auto-expand section when active tab changes
  useEffect(() => {
    if (activeSection !== "main") {
      setExpandedSections((prev) => new Set([...prev, activeSection]));
    }
  }, [activeSection]);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
      // logout clears auth -> the app redirects to Login on its
      // own. The overlay stays up through that transition so the
      // user always sees something happening.
    } catch {
      // if it fails, drop the overlay so they can retry
      setSigningOut(false);
    }
  }

  const grouped = SECTION_ORDER.map((sec) => ({
    sec,
    items: visible.filter((i) => sectionOfItem(i) === sec)
      .sort((a, b) => rankIn(sec, a.id) - rankIn(sec, b.id)),
  }));

  return (
    <div className="min-h-screen bg-[#f4f6fa] dark:bg-[#0e1830] flex">
      <SessionTimeoutModal />
      <BackupHealthAlert />
      {signingOut && (
        <div className="fixed inset-0 z-[100] bg-[#0e1830]/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 rounded-2xl bg-white dark:bg-gray-800 px-10 py-8 shadow-2xl">
            <Loader2 size={36} className="animate-spin text-primary" />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Signing you out…
            </p>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 ${
          collapsed ? "w-72 lg:w-20" : "w-72"
        } h-screen lg:h-screen bg-gradient-to-b from-primary to-primaryDark text-white flex flex-col transition-all duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <SchoolLogo size={40} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm leading-tight truncate">
                {SCHOOL.platformNameShort}
              </p>
              <p className="text-[11px] text-white/50 truncate">
                {SCHOOL.motto}
              </p>
            </div>
          )}
          {/* collapse toggle — desktop only */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="hidden lg:flex items-center justify-center w-7 h-7 rounded-lg text-white/60 hover:bg-white/10 hover:text-white shrink-0"
            title={collapsed ? "Expand menu" : "Collapse menu"}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} />
            ) : (
              <PanelLeftClose size={18} />
            )}
          </button>
        </div>

        <nav className="nav-scroll flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {grouped.map(({ sec, items }) => {
            if (items.length === 0) return null;
            const label = sectionLabel(sec, userRoles);
            const isMain = sec === "main";
            const isExpanded = isMain || collapsed || expandedSections.has(sec);
            const hasActiveItem = items.some((i) => i.id === active);

            return (
              <div key={sec}>
                {/* Section header — collapsible for non-main sections */}
                {label && !collapsed && (
                  <button
                    onClick={() => toggleSection(sec)}
                    className="w-full flex items-center justify-between px-3 py-2 mb-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      {label}
                      {!isExpanded && hasActiveItem && (
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                      )}
                    </span>
                    <ChevronDown
                      size={12}
                      className={`transition-transform duration-200 ${
                        isExpanded ? "rotate-0" : "-rotate-90"
                      }`}
                    />
                  </button>
                )}
                {/* Items — shown/hidden based on expanded state */}
                {isExpanded && (
                  <ul className="space-y-0.5">
                    {items.map((item) => {
                      const Icon = item.icon;
                      const isActive = active === item.id;
                      return (
                        <li key={item.id}>
                          <button
                            onClick={() => {
                              // Nako's tab must open inside the click itself.
                              if (item.id === "nako") launchNako();
                              onNavigate(item.id);
                              setMobileOpen(false);
                            }}
                            title={collapsed ? item.label : undefined}
                            className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                              collapsed ? "lg:justify-center" : ""
                            } ${
                              isActive
                                ? "bg-white text-primary font-semibold shadow-sm"
                                : "text-white/80 hover:bg-white/10"
                            }`}
                          >
                            <Icon size={17} className="shrink-0" />
                            {!collapsed && item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-white/10 shrink-0">
          <div
            className={`flex items-center gap-2 mb-3 ${
              collapsed ? "lg:justify-center" : ""
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-secondary text-ink flex items-center justify-center text-xs font-bold shrink-0">
              {(userData?.displayName ?? "U").charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate">
                  {userData?.displayName}
                </p>
                {roleMeta && (
                  <p className="text-[10px] text-white/50">{roleMeta.label}</p>
                )}
              </div>
            )}
          </div>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 disabled:opacity-60 ${
              collapsed ? "lg:justify-center" : ""
            }`}
          >
            <LogOut size={16} className="shrink-0" />
            {!collapsed && (signingOut ? "Signing out…" : "Sign out")}
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-gray-600 dark:text-gray-300"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h1 className="font-semibold text-gray-800 dark:text-gray-100">
              {NAV.find((n) => n.id === active)?.label ?? "Dashboard"}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <PushEnrollButton />
            <NotificationsPanel
              onOpenFlag={(ctx) => {
                if (ctx?.classId) {
                  // hand the target to CommentReview via a
                  // one-shot transient marker (navigation intent,
                  // cleared on read — not persisted state).
                  try {
                    sessionStorage.setItem(
                      "lkc.flagTarget",
                      JSON.stringify(ctx),
                    );
                  } catch {
                    /* ignore */
                  }
                }
                onNavigate("commentReview");
              }}
              onOpenFlaggedComment={(d) => {
                // Teacher opening their own flagged comment. Drop a one-shot
                // marker the target screen reads to preselect + highlight, then
                // route to where that comment is edited.
                try {
                  sessionStorage.setItem("lkc.commentFlagFix", JSON.stringify(d));
                } catch {
                  /* ignore */
                }
                const tab =
                  d.target === "classTeacherComment"
                    ? "classComment"
                    : d.target === "hodComment"
                      ? "hodComment"
                      : "marks";
                onNavigate(tab);
              }}
            />
            <select
              value={year}
              onChange={(e) => onYearChange(e.target.value)}
              className="text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5"
            >
              {SCHOOL.selectableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <select
              value={term}
              onChange={(e) => onTermChange(e.target.value as TermKey)}
              className="text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5"
            >
              {(Object.keys(SCHOOL.terms) as TermKey[]).map((t) => (
                <option key={t} value={t}>
                  {SCHOOL.terms[t].label}
                </option>
              ))}
            </select>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
